import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

/** Build a compact list of page numbers around `current`, with ellipses. */
function pageRange(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: (number | "…")[] = [];
  const window = 1;
  const left = Math.max(2, current - window);
  const right = Math.min(total - 1, current + window);
  out.push(1);
  if (left > 2) out.push("…");
  for (let i = left; i <= right; i++) out.push(i);
  if (right < total - 1) out.push("…");
  out.push(total);
  return out;
}

export function Pagination({
  page,
  totalPages,
  onPageChange,
  disabled,
}: {
  page: number;
  totalPages: number;
  onPageChange: (next: number) => void;
  disabled?: boolean;
}) {
  if (totalPages <= 1) return null;
  const safePage = Math.min(Math.max(1, page), totalPages);
  const items = pageRange(safePage, totalPages);

  const goto = (n: number) => {
    if (disabled || n === safePage || n < 1 || n > totalPages) return;
    onPageChange(n);
  };

  const btnBase =
    "h-8 min-w-8 px-2 inline-flex items-center justify-center rounded text-sm font-medium border transition disabled:opacity-40 disabled:cursor-not-allowed";
  const btnIdle =
    "border-canvas-border dark:border-surface-border bg-canvas-raised dark:bg-surface-raised hover:bg-canvas-subtle dark:hover:bg-surface-border";
  const btnActive =
    "border-brand-greenAlt bg-brand-green text-slate-900 cursor-default";

  return (
    <nav
      className="flex flex-wrap items-center justify-center gap-1.5 py-3"
      aria-label="Pagination"
    >
      <button
        type="button"
        onClick={() => goto(1)}
        disabled={disabled || safePage === 1}
        className={`${btnBase} ${btnIdle}`}
        aria-label="First page"
      >
        <ChevronsLeft size={14} />
      </button>
      <button
        type="button"
        onClick={() => goto(safePage - 1)}
        disabled={disabled || safePage === 1}
        className={`${btnBase} ${btnIdle}`}
        aria-label="Previous page"
      >
        <ChevronLeft size={14} />
      </button>

      {items.map((it, idx) =>
        it === "…" ? (
          <span
            key={`e${idx}`}
            className="px-1.5 text-slate-400 dark:text-slate-500 select-none"
            aria-hidden
          >
            …
          </span>
        ) : (
          <button
            key={it}
            type="button"
            onClick={() => goto(it)}
            disabled={disabled}
            className={`${btnBase} ${it === safePage ? btnActive : btnIdle}`}
            aria-current={it === safePage ? "page" : undefined}
            aria-label={`Page ${it}`}
          >
            {it}
          </button>
        ),
      )}

      <button
        type="button"
        onClick={() => goto(safePage + 1)}
        disabled={disabled || safePage === totalPages}
        className={`${btnBase} ${btnIdle}`}
        aria-label="Next page"
      >
        <ChevronRight size={14} />
      </button>
      <button
        type="button"
        onClick={() => goto(totalPages)}
        disabled={disabled || safePage === totalPages}
        className={`${btnBase} ${btnIdle}`}
        aria-label="Last page"
      >
        <ChevronsRight size={14} />
      </button>
    </nav>
  );
}
